import type { ViewRow } from "@/lib/boardView";
import { isWorkflowStatusColumn } from "@/lib/statusRoles";
import type { StatusRole } from "@/lib/types";

/** Whether a board's status groups may be deleted as a whole. Only CATEGORY groups on a
 * plain project board (e.g. an Aktionen board's "Gruppe"): never a workflow Status column
 * (deleting "Erledigt" would soft-delete every done row and archive an option the workflow
 * depends on — role-less legacy labels count, via roleOf), and never on ticket / idea /
 * Aufgaben boards, whose rows carry a workflow or feed a Protokoll. */
export function canDeleteGroup(
  board: { kind: string; is_aufgaben?: boolean },
  column:
    | { type?: string; options?: ({ role?: StatusRole | null; label?: string } | null)[] | null }
    | null
    | undefined,
): boolean {
  if (board.kind !== "project" || board.is_aufgaben) return false;
  if (!column || column.type !== "status") return false;
  return !isWorkflowStatusColumn(column);
}

/** The year tab to keep once the set of years with sections changed. Falls back to "Alle"
 * (null) when the selected year has no section left — deleting a year's only section used
 * to leave the board blank — or when fewer than two years remain: the tab bar only renders
 * from two years up, so a surviving filter would hide the year-less sections with no tab
 * left to switch back. */
export function reconcileYearFilter(yearFilter: number | null, availableYears: number[]) {
  if (yearFilter === null) return null;
  return availableYears.length < 2 || !availableYears.includes(yearFilter) ? null : yearFilter;
}

/** What deleting a status group removes: its TOP-LEVEL rows (their sub-items go with
 * them — the server cascades, and a sub-item belongs to its parent's group whatever its
 * own cell says) plus how many sub-items that takes along, for the confirm message.
 * Pass the board's FULL rows, not a filtered view: the whole table goes, including rows
 * an active filter happens to hide. */
export interface GroupDeletePlan {
  rowIds: string[];
  subitemCount: number;
}

export function planGroupDelete(
  rows: ViewRow[],
  groupColumnId: string,
  optionId: string,
): GroupDeletePlan {
  const rowIds = rows
    .filter(
      (r) =>
        !r.parentRowId &&
        r.raw?.cells?.find((c) => c.column_def_id === groupColumnId)?.value === optionId,
    )
    .map((r) => r.id);
  const ids = new Set(rowIds);
  const subitemCount = rows.filter((r) => r.parentRowId && ids.has(r.parentRowId)).length;
  return { rowIds, subitemCount };
}

/** Status options with `optionId` archived (or un-archived). Archiving is how an option
 * is "removed" everywhere in Flow: its id keeps resolving for any cell still holding it,
 * but pickers stop offering it. Un-archiving drops the flag entirely. */
export function withOptionArchived<T extends { id: string; archived?: boolean }>(
  options: T[],
  optionId: string,
  archived: boolean,
): T[] {
  return options.map((o) => {
    if (o.id !== optionId) return o;
    if (archived) return { ...o, archived: true };
    const { archived: _dropped, ...rest } = o;
    return rest as T;
  });
}
